/**
 * Domain-neutral canvas topology provider for CAP-3 / Addendum 2 §6.
 *
 * Enumerates referenceable nodes on one render surface. The host injects
 * structure + live data-source name lookup; this module performs no I/O and
 * imports no React so the `ce-editor` bundle stays server-safe.
 */

import type { FieldOption } from '../types';

/**
 * A referenceable node on a canvas surface.
 *
 * - `id` — stable authored identity (host: e.g. Puck `props.id`)
 * - `label` — author-facing display name (host: e.g. `resolveBlockLabel`)
 * - `kind` — component type string; host-defined, no CE enum of kinds
 * - `container` — `true` ⇒ groupable (downstream walks to leaves)
 * - `dataSourceNames` — live names beneath this node, resolved at `list()` time
 */
export interface CanvasTopologyNode {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
  readonly container: boolean;
  readonly dataSourceNames: readonly string[];
}

/**
 * Scope for a topology `list()` call.
 * Omit / leave `surfaceId` undefined ⇒ host default surface.
 */
export interface CanvasTopologyScope {
  readonly surfaceId?: string;
}

/**
 * Host-facing contract: what components on this surface can be referenced.
 * Separate from `OptionsProvider` (field values); downstream field-option
 * consumers bridge via `topologyToFieldOptions`.
 */
export interface CanvasTopologyProvider {
  list(scope?: CanvasTopologyScope): ReadonlyArray<CanvasTopologyNode>;
}

/**
 * Injectable enumeration + live name lookup. CE does not know the host tree
 * walker or registry keys — the host supplies both.
 *
 * Host contract:
 * - `id` = stable authored identity, unique within a surface. Duplicate ids
 *   are a host bug: the provider does not dedupe, so a repeated id yields
 *   repeated nodes and repeated `resolveDataSourceNames` calls.
 * - `label` = author-facing display name
 * - `enumerate` returns nodes in canvas order for the requested surface;
 *   surface scoping must match mount reality (inactive surfaces absent)
 * - `resolveDataSourceNames` returns currently registered host source keys for
 *   that node — never a value persisted into the DSL. Called on every `list()`;
 *   must not be memoized inside the provider across calls.
 * - Both callbacks are synchronous, matching a `list()` that reads an
 *   in-memory tree and registry. Async resolution would be a contract change.
 */
export interface CanvasTopologySource {
  /** Structural nodes for a surface, in canvas order — no live names yet. */
  enumerate(
    scope: CanvasTopologyScope,
  ): ReadonlyArray<Omit<CanvasTopologyNode, 'dataSourceNames'>>;

  /**
   * Live data-source names for a node id. Called on every `list()` —
   * must not be memoized inside the provider across calls.
   */
  resolveDataSourceNames(nodeId: string): readonly string[];
}

/**
 * Resolves `dataSourceNames` on every `list()` via the injected source.
 * Stateless wrapper — safe to share a single instance across consumers.
 */
export class InjectableCanvasTopologyProvider
  implements CanvasTopologyProvider
{
  constructor(private readonly source: CanvasTopologySource) {}

  list(scope: CanvasTopologyScope = {}): ReadonlyArray<CanvasTopologyNode> {
    return this.source.enumerate(scope).map((node) => ({
      ...node,
      dataSourceNames: this.source.resolveDataSourceNames(node.id),
    }));
  }
}

/**
 * Map topology nodes → FieldOption[] for CAP-3 / buildResolveFields consumers.
 * Only `id` → `value` and `label` → `label`; does not copy `dataSourceNames`.
 */
export function topologyToFieldOptions(
  nodes: ReadonlyArray<Pick<CanvasTopologyNode, 'id' | 'label'>>,
): FieldOption[] {
  return nodes.map((node) => ({ value: node.id, label: node.label }));
}
